import Anthropic from '@anthropic-ai/sdk';
import { MessageStreamEvent } from '@anthropic-ai/sdk/resources'; // Import stream event type
import { Message as AppMessage } from './types'; // Import our app's Message type
import { modelHasCapability } from './models'; // Import modelHasCapability

// Initialize the Anthropic client using the environment variable
const anthropic = new Anthropic({
    apiKey: process.env.REACT_APP_ANTHROPIC_API_KEY, dangerouslyAllowBrowser: true,
});

/**
 * Converts our app's message format to Anthropic's message format,
 * extracting the system prompt.
 * 
 * @param messages Array of messages in our app's format
 * @returns An object containing the formatted messages (without system prompt) 
 *          and the extracted system prompt content (if any).
 */
const processMessagesForAnthropic = (messages: any[]): { systemPrompt: string | undefined; formattedMessages: any[] } => {
    let systemPrompt: string | undefined = undefined;
    const formattedMessages: any[] = [];

    messages.forEach(msg => {
        if (msg.role === 'system') {
            // Extract system prompt content
            systemPrompt = typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content);
        } else {
            // Keep user and assistant messages
            let role: 'user' | 'assistant' = 'user'; // Default to user
            if (msg.role === 'assistant') role = 'assistant';

            let content = msg.content;
            if (typeof content !== 'string') {
                content = JSON.stringify(content);
            }

            formattedMessages.push({ role, content });
        }
    });

    return { systemPrompt, formattedMessages };
};

/**
 * Sends a message to the specified Anthropic model and returns a stream.
 *
 * @param model The ID of the Anthropic model to use.
 * @param messages An array of message objects representing the conversation history.
 * @param maxTokens The maximum number of tokens to generate.
 * @returns An async iterable stream of message events from the Anthropic API.
 */
export const streamAnthropicMessage = async (
    model: string,
    messages: any[],
    maxTokens: number = 1024
): Promise<AsyncIterable<MessageStreamEvent>> => { // Update return type
    try {
        const { systemPrompt, formattedMessages } = processMessagesForAnthropic(messages);

        const requestBody: Anthropic.MessageCreateParams = {
            model: model,
            max_tokens: maxTokens,
            system: systemPrompt,
            messages: formattedMessages,
            stream: true // Enable streaming
        };

        if (modelHasCapability(model, 'reasoning')) {
            requestBody.thinking = {
                type: "enabled",
                budget_tokens: 1024
            };
            requestBody.max_tokens = Math.max(maxTokens, 2048);
        }

        const stream = await anthropic.messages.stream(requestBody); // Use .stream()
        return stream;
    } catch (error) {
        console.error('Error calling Anthropic API stream:', error);
        throw error;
    }
};

// Example usage (can be removed or adapted later)
/*
async function testAnthropic() {
    const exampleMessages: MessageParam[] = [{ role: "user", content: "Hello, Claude" }];
    const modelId = "claude-3-7-sonnet-20250219"; // Use the correct model ID

    try {
        const response = await sendAnthropicMessage(modelId, exampleMessages);
        console.log("Test successful:", response);
    } catch (error) {
        console.error("Test failed:", error);
    }
}

// testAnthropic(); // Uncomment to run the test
*/ 